import React from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { LetterFormValues } from "@/types/elf-letter";
import { Link } from "react-router-dom";

interface LetterFormProps {
  values: LetterFormValues;
  onChange: (values: Partial<LetterFormValues>) => void;
  onSubmit: () => void;
  isGenerating: boolean;
  disabled?: boolean;
  needsUpdate?: boolean;
}

const LetterForm: React.FC<LetterFormProps> = ({
  values,
  onChange,
  onSubmit,
  isGenerating,
  disabled = false,
  needsUpdate = false,
}) => {
  return (
    <div className="space-y-4 md:space-y-6">
      <div className="space-y-1.5 md:space-y-2">
        <Label htmlFor="childName" className="text-sm">Child&apos;s First Name or Nickname</Label>
        <Input
          id="childName"
          disabled={disabled}
          value={values.childName}
          onChange={(event) =>
            onChange({ childName: event.currentTarget.value })
          }
          placeholder="Addison"
          maxLength={120}
          className="h-11 md:h-12 rounded-xl border-muted focus-visible:ring-slate-700 text-base"
        />
      </div>

      <div className="space-y-1.5 md:space-y-2">
        <Label htmlFor="elfName" className="text-sm">Your Elf&apos;s Name</Label>
        <Input
          id="elfName"
          disabled={disabled}
          value={values.elfName}
          onChange={(event) =>
            onChange({ elfName: event.currentTarget.value })
          }
          placeholder="Sparkle Jingleberry"
          maxLength={120}
          className="h-11 md:h-12 rounded-xl border-muted focus-visible:ring-slate-700 text-base"
        />
      </div>

      <div className="space-y-1.5 md:space-y-2">
        <Label htmlFor="specialInstructions" className="text-sm">
          Special Details to Mention
        </Label>
        <Textarea
          id="specialInstructions"
          disabled={disabled}
          value={values.specialInstructions}
          onChange={(event) =>
            onChange({ specialInstructions: event.currentTarget.value })
          }
          placeholder="Mention how much she loved the gingerbread house last night!"
          maxLength={600}
          rows={3}
          className="rounded-2xl border-muted focus-visible:ring-slate-700 resize-none text-base min-h-[100px]"
        />
        <p className="text-xs text-muted-foreground">
          A favorite activity or kind moment is enough. Leave out surnames, addresses, school names, and sensitive details.
        </p>
      </div>

      <p className="text-xs leading-relaxed text-muted-foreground">
        For parents and caregivers. We send the details you enter to our AI provider to write the letter and store those details and the generated letter on our servers. Review the wording before sharing it with your child.{" "}
        <Link to="/privacy/#collection" className="underline underline-offset-4">How your information is used</Link>
      </p>
      <Button
        onClick={onSubmit}
        disabled={disabled || isGenerating}
        className="w-full h-11 md:h-12 rounded-xl bg-elfRed-ink hover:bg-[#892638] text-white focus-visible:ring-slate-700 font-semibold tracking-wide transition text-sm md:text-base"
      >
        {isGenerating ? "Generating..." : needsUpdate ? "Update Letter" : "Generate Letter"}
      </Button>
    </div>
  );
};

export default LetterForm;

